import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { authApi } from '../api/auth';
import { AuditLogResponse, PageResponse } from '../types';
import { History, RefreshCcw, Search, ChevronLeft, ChevronRight, SlidersHorizontal } from 'lucide-react';
import LoadingScreen from '../components/LoadingScreen';

type Filters = {
  keyword: string;
  from: string;
};

const formatDateTime = (value?: string | null) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getAuditBadgeClass = (action: string) => {
  const upper = action.toUpperCase();
  if (upper.includes('FAILED') || upper.includes('REJECTED') || upper.includes('REVOKED') || upper.includes('DELETED') || upper.includes('REMOVED')) {
    return 'badge-locked';
  }
  if (upper.includes('SUCCESS') || upper.includes('APPROVED') || upper.includes('GRANTED') || upper.includes('CREATED') || upper.includes('UPDATED') || upper.includes('ADDED') || upper.includes('CONFIRMED') || upper.includes('REISSUE')) {
    return 'badge-active';
  }
  return 'badge-inactive';
};

const initialFilters: Filters = {
  keyword: '',
  from: '',
};

const AuditLogsPage: React.FC = () => {
  const [filters, setFilters] = useState<Filters>(initialFilters);
  const [showAuthLogs, setShowAuthLogs] = useState(false);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(50);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [pageData, setPageData] = useState<PageResponse<AuditLogResponse> | null>(null);

  const totalElements = pageData?.totalElements ?? 0;
  const totalPages = pageData?.totalPages ?? 0;

  const buildQuery = useCallback(
    (pageToLoad: number, currentFilters: Filters, currentSize: number) => {
      return {
        keyword: currentFilters.keyword.trim() || undefined,
        from: currentFilters.from ? new Date(currentFilters.from).toISOString() : undefined,
        page: pageToLoad,
        size: currentSize,
      };
    },
    []
  );

  const fetchLogs = useCallback(
    async (pageToLoad: number, nextFilters: Filters, nextSize: number) => {
      if (pageToLoad === 0) {
        setLoading(true);
      } else {
        setRefreshing(true);
      }

      setError('');
      try {
        const response = await authApi.getAuditLogs(buildQuery(pageToLoad, nextFilters, nextSize));
        const result = response.data.result;
        setPageData(result);
        setPage(pageToLoad);
      } catch (err: any) {
        setError(err?.response?.data?.message || '로그를 불러오지 못했습니다.');
        setPageData(null);
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [buildQuery]
  );

  useEffect(() => {
    fetchLogs(0, initialFilters, 50);
  }, [fetchLogs]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await fetchLogs(0, filters, size);
  };

  const handleReset = async () => {
    setFilters(initialFilters);
    await fetchLogs(0, initialFilters, size);
  };

  const currentLogs = useMemo(() => pageData?.content ?? [], [pageData]);
  const visibleLogs = useMemo(() => {
    if (showAuthLogs) return currentLogs;
    const authActions = new Set([
      'LOGIN_SUCCESS',
      'LOGIN_FAILED',
      'LOGOUT',
      'TOKEN_REISSUE',
      'PASSWORD_RESET_REQUESTED',
      'PASSWORD_RESET_CONFIRMED',
    ]);
    return currentLogs.filter((log) => !authActions.has(log.action));
  }, [currentLogs, showAuthLogs]);

  if (loading) return <LoadingScreen message="로그를 불러오는 중" />;

  return (
    <div className="fade-in">
      <section className="card dashboard-hero-card" style={{ marginBottom: '1rem' }}>
        <div className="dashboard-hero-grid">
          <div className="dashboard-hero-copy">
            <div className="dashboard-page-kicker">
              <History size={12} />
              <span>로그 관리</span>
            </div>
            <h1 className="dashboard-hero-title" style={{ fontSize: '1.45rem', fontWeight: 700 }}>
              관리자 감사 로그를 확인합니다.
            </h1>
            <p className="dashboard-hero-description">
              로그인, 회원가입, 승인, 서비스 변경, 멤버 변경, 광고 티켓 변경 이력을 필터링해서 볼 수 있습니다.
            </p>
          </div>

          <div className="dashboard-hero-stats" style={{ gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' }}>
            <div className="dashboard-stat-chip">
              <span>총 로그</span>
              <strong>{totalElements}</strong>
            </div>
            <div className="dashboard-stat-chip">
              <span>현재 페이지</span>
              <strong>{page + 1}</strong>
            </div>
            <div className="dashboard-stat-chip">
              <span>페이지 크기</span>
              <strong>{size}</strong>
            </div>
          </div>
        </div>
      </section>

      <section className="card dashboard-main-card" style={{ marginBottom: '1rem' }}>
        <div className="dashboard-card-head">
          <div className="dashboard-card-kicker">
            <SlidersHorizontal size={14} />
            <span>필터</span>
          </div>
          <h2 className="dashboard-card-title dashboard-card-title-sm">조건을 적용해 로그를 좁혀보세요.</h2>
        </div>

        <form onSubmit={handleSubmit} className="audit-filter-grid">
          <label className="dashboard-request-field">
            <span className="label">이름 또는 이메일 검색</span>
            <input
              className="input"
              value={filters.keyword}
              onChange={(e) => setFilters((prev) => ({ ...prev, keyword: e.target.value }))}
              placeholder="검색어 입력"
            />
          </label>

          <label className="dashboard-request-field">
            <span className="label">시작 일자</span>
            <input
              className="input"
              type="date"
              value={filters.from}
              onChange={(e) => setFilters((prev) => ({ ...prev, from: e.target.value }))}
            />
          </label>

          <div className="dashboard-request-field">
            <span className="label">페이지 크기</span>
            <select className="input" value={size} onChange={(e) => setSize(Number(e.target.value))}>
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>

          <label className="dashboard-request-field audit-checkbox-field">
            <input
              type="checkbox"
              checked={showAuthLogs}
              onChange={(e) => setShowAuthLogs(e.target.checked)}
            />
            <span className="label">로그인 로그 표시</span>
          </label>

          <div className="audit-filter-actions">
            <button className="btn btn-primary" type="submit">
              <Search size={16} /> 조회
            </button>
            <button className="btn btn-outline" type="button" onClick={handleReset}>
              초기화
            </button>
            <button className="btn btn-outline" type="button" onClick={() => fetchLogs(page, filters, size)} disabled={refreshing}>
              <RefreshCcw size={16} className={refreshing ? 'animate-spin' : ''} /> 새로고침
            </button>
          </div>
        </form>
      </section>

      {error && (
        <div className="dashboard-empty-note" style={{ color: 'var(--error)', padding: '0 0 1rem 0' }}>
          {error}
        </div>
      )}

      <section className="card dashboard-main-card" style={{ padding: 0, overflow: 'hidden' }}>
          <div className="audit-table-head">
            <div>시간</div>
            <div>액션</div>
            <div>actor</div>
            <div>target</div>
            <div>service</div>
            <div>userAgent</div>
          </div>

        {visibleLogs.length > 0 ? (
          <div className="audit-table-body">
            {visibleLogs.map((log) => (
              <div key={log.id} className="audit-table-row">
                <div className="audit-table-cell">{formatDateTime(log.createdAt)}</div>
                <div className="audit-table-cell audit-action-cell">
                  <span className={`badge ${getAuditBadgeClass(log.action)} audit-action-badge`}>{log.action}</span>
                </div>
                <div className="audit-table-cell">
                  {log.actorEmail ? (
                    <>
                      <div style={{ fontWeight: 700 }}>{log.actorUserId}</div>
                      <span className="audit-secondary">{log.actorEmail}</span>
                    </>
                  ) : (
                    <>
                      <div style={{ fontWeight: 700 }}>{log.actorUserId}</div>
                      <span className="audit-secondary">-</span>
                    </>
                  )}
                </div>
                <div className="audit-table-cell">
                  {log.targetType}
                  {log.targetId !== null && <span className="audit-secondary">#{log.targetId}</span>}
                </div>
                <div className="audit-table-cell">{log.serviceCode || '-'}</div>
                <div className="audit-table-cell audit-user-agent" title={log.userAgent || '-'}>
                  {log.userAgent || '-'}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="dashboard-empty-note" style={{ padding: '2rem' }}>
            조회된 로그가 없습니다.
          </div>
        )}
      </section>

      <div className="audit-pagination">
        <button className="btn btn-outline" type="button" onClick={() => fetchLogs(Math.max(0, page - 1), filters, size)} disabled={page <= 0 || refreshing}>
          <ChevronLeft size={16} /> 이전
        </button>

        <div className="audit-pagination-info">
          {totalPages > 0 ? `${page + 1} / ${totalPages}` : '0 / 0'}
        </div>

        <button
          className="btn btn-outline"
          type="button"
          onClick={() => fetchLogs(page + 1, filters, size)}
          disabled={(pageData?.last ?? true) || refreshing}
        >
          다음 <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );
};

export default AuditLogsPage;
